/* ═══════════════════════════════════════════════════════════════
   MODULE MODE for the Studio canvas (docs/MODULES_R2_PLAN.md Phase 4).

   ⛔ A MODULE is a reusable block-group, single-spec only — never a variant.

   Two things the canvas gains when it is editing a MODULE instead of a spec:

     · the PART a level-2 article declares (1/2/3) — the only address a module
       has when it lands (`*.##`, R2 Ruling 6). Written straight into the
       node's `meta`, so it round-trips through `docToBlocks` untouched and
       `moduleChunksFromBlocks` reads it back off the block.
     · the inline FLAGS the structure law raises. They are decorations, not
       dialogs: a broken rule is shown ON the paragraph that breaks it and the
       Save button stands down (Ruling 6, "inline flags, not error dialogs").

   The law itself lives in `shared/spec-modules.ts` and is tested there. This
   file only paints what that function says.
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Fragment, Slice } from '@tiptap/pm/model';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { MODULE_PARTS, type ModulePart, type ModuleViolation } from '../../shared/spec-modules';
import { topBlocks, type TopBlock } from '../designer/moduleInsert';
import { expandToGroups } from '../designer/commands';
import { snapBoundaryOutOfGroup } from '../../shared/block-groups';
import { newBlockId } from '../designer/schema';

type Dispatch = ((tr: Transaction) => void) | undefined;

/** Violations are derived OUTSIDE the document (from the block list the
 *  builder holds), so a change in them is not a doc change. This meta tells
 *  the plugin to re-read them anyway — the same trick the variant uses. */
export const MODULE_FLAGS_REBUILD = 'sfModuleFlagsRebuild';

export const moduleFlagsKey = new PluginKey<{ deco: DecorationSet }>('moduleBuildFlags');

export interface ModuleFlagOptions {
  /** Block id → the one violation shown on it. */
  getViolations: () => Record<string, ModuleViolation>;
}

function build(state: EditorState, violations: Record<string, ModuleViolation>): DecorationSet {
  const decorations: Decoration[] = [];
  for (const block of topBlocks(state)) {
    const violation = block.id ? violations[block.id] : undefined;
    if (!violation) continue;
    decorations.push(
      Decoration.node(block.pos, block.pos + block.node.nodeSize, {
        class: `sps-mod-flagged is-${violation.kind}`,
        // The message rides as an attribute and is drawn by CSS, so the flag
        // adds no editable content to the paragraph it is warning about.
        'data-sf-modflag': violation.message,
      })
    );
  }
  return decorations.length ? DecorationSet.create(state.doc, decorations) : DecorationSet.empty;
}

/** The inline structure flags. Wired into the canvas only in module mode. */
export const ModuleBuildFlags = Extension.create<ModuleFlagOptions>({
  name: 'moduleBuildFlags',

  addOptions() {
    return { getViolations: () => ({}) };
  },

  addProseMirrorPlugins() {
    const getViolations = () => this.options.getViolations();
    return [
      new Plugin<{ deco: DecorationSet }>({
        key: moduleFlagsKey,
        state: {
          init: (_config, state) => ({ deco: build(state, getViolations()) }),
          apply: (tr, value, _old, newState) =>
            tr.docChanged || tr.getMeta(MODULE_FLAGS_REBUILD)
              ? { deco: build(newState, getViolations()) }
              : value,
        },
        props: {
          decorations: (state) => moduleFlagsKey.getState(state)?.deco ?? DecorationSet.empty,
        },
      }),
    ];
  },
});

/** Re-read the violations without touching the document (and without making
 *  it dirty or adding an undo step). */
export function repaintModuleFlags(state: EditorState, dispatch: Dispatch): boolean {
  if (!dispatch) return false;
  dispatch(state.tr.setMeta(MODULE_FLAGS_REBUILD, true).setMeta('addToHistory', false));
  return true;
}

/** Every level-2 article, with the part it currently declares. */
export function moduleArticles(
  state: EditorState
): Array<{ id: string; part: ModulePart | undefined }> {
  return topBlocks(state)
    .filter((b) => (b.node.attrs.level as number) === 2 && !!b.id)
    .map((b) => {
      const meta = (b.node.attrs.meta as Record<string, unknown>) || {};
      const raw = meta.modulePart;
      return {
        id: b.id,
        part: raw === 1 || raw === 2 || raw === 3 ? (raw as ModulePart) : undefined,
      };
    });
}

/**
 * Assign (or clear) the part on ONE article. One transaction, so one Ctrl+Z
 * takes it back like any other edit — the part IS part of the definition.
 */
export function setModulePart(
  state: EditorState,
  dispatch: Dispatch,
  blockId: string,
  part: ModulePart | null
): boolean {
  const target = topBlocks(state).find((b) => b.id === blockId);
  if (!target) return false;
  const meta = { ...((target.node.attrs.meta as Record<string, unknown>) || {}) };
  if (part) meta.modulePart = part;
  else delete meta.modulePart;
  const tr = state.tr.setNodeMarkup(target.pos, undefined, { ...target.node.attrs, meta });
  if (dispatch) dispatch(tr);
  return true;
}

/* ═══════════════════════════════════════════════════════════════
   THE THREE PART HEADERS (Cohesion R1 Phase 1).

   "PART 1 - GENERAL" / "PART 2 - PRODUCTS" / "PART 3 - EXECUTION" read as
   the module's structure, but they are never document content — a saved
   PART heading would insert a second one into the target spec
   (moduleChunksFromBlocks already says so). So they are DECORATIONS: one
   widget per part, positioned just before that part's first article, which
   is what makes the flat block list read as three grouped sections.
   ═══════════════════════════════════════════════════════════════ */

export const PART_HEADER_TITLES: Record<ModulePart, string> = {
  1: 'PART 1 - GENERAL',
  2: 'PART 2 - PRODUCTS',
  3: 'PART 3 - EXECUTION',
};

export const modulePartHeadersKey = new PluginKey<DecorationSet>('moduleBuildPartHeaders');

function headerWidget(part: ModulePart, hasContent: boolean): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'sps-mod-part-hdr';
  wrap.setAttribute('contenteditable', 'false');
  // Read by the drag machinery (BlockGutter, the toolbox's "New paragraph"
  // drag) to hit-test a drop against a real part rather than guessing from
  // neighboring blocks — a header renders exactly at its group's start (or,
  // for an empty part, exactly where that group's first article would land),
  // so its own rect IS the group's drop zone (Module Zones Phase 2/3).
  wrap.setAttribute('data-sf-part-hdr', String(part));
  const title = document.createElement('div');
  title.className = 'sps-mod-part-hdr-text';
  title.textContent = PART_HEADER_TITLES[part];
  wrap.appendChild(title);
  if (!hasContent) {
    const hint = document.createElement('div');
    hint.className = 'sps-mod-part-hdr-hint';
    hint.textContent = 'No articles yet';
    wrap.appendChild(hint);
  }
  return wrap;
}

/**
 * The UNASSIGNED group's header (Josh's ruling, 2026-08-31): a clearly
 * designated fourth area so an article nobody has placed yet can never be
 * mistaken for one that lives in a part.
 *
 * Deliberately NOT tagged `data-sf-part-hdr`. The drag machinery
 * (`BlockGutter`, the toolbox's "New paragraph" drag) hit-tests part
 * headers only, so this is a LABEL and never a drop zone: it never
 * highlights, it is never a valid header-drop target, and no drag through
 * it assigns or unassigns anything.
 */
function unassignedHeaderWidget(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'sps-mod-unassigned-hdr';
  wrap.setAttribute('contenteditable', 'false');
  wrap.setAttribute('data-sf-unassigned-hdr', '1');
  const title = document.createElement('div');
  title.className = 'sps-mod-unassigned-hdr-text';
  title.textContent = 'Unassigned';
  wrap.appendChild(title);
  const hint = document.createElement('div');
  hint.className = 'sps-mod-unassigned-hdr-hint';
  hint.textContent = 'Not in a part yet — pick a part for each of these.';
  wrap.appendChild(hint);
  return wrap;
}

export interface ModuleHeaderLayout {
  /** Where each PART header renders. */
  positions: Record<ModulePart, number>;
  /** Whether that part has any article yet (drives the "No articles yet"
   *  hint, and therefore the widget's key). */
  hasContent: Record<ModulePart, boolean>;
  /** Where the UNASSIGNED header renders — absent when every article has a
   *  part, which is the state a finished module is in. */
  unassignedPos?: number;
}

/** Everything the header decorations are derived from, as data — so the
 *  grouping law can be proved without a browser (`moduleZones.test.ts`). */
export function modulePartHeaderLayout(state: EditorState): ModuleHeaderLayout {
  const blocks = topBlocks(state);
  const firstPos: Partial<Record<ModulePart, number>> = {};
  // Where the unassigned group starts. `ModuleDefaultPart` stamps any
  // article that follows an assigned one, so in practice this group is the
  // contiguous run at the TOP of the document — but a module loaded with
  // `partIndex: 0` chunks can hold one anywhere until the first edit heals
  // it, and the header belongs WITH its group rather than lying about
  // where the group is.
  let firstUnassignedPos: number | undefined;
  const hasArticle: Record<ModulePart, boolean> = { 1: false, 2: false, 3: false };
  for (const b of blocks) {
    const level = b.node.attrs.level as number;
    if (level > 2) {
      // A paragraph PINNED to a part that has no article yet (TWEAKS item
      // 10) opens that part's group, so the header renders right above it —
      // but it is not an article, so the "No articles yet" hint stays.
      const pin = paragraphPin(b.node);
      if (pin !== undefined && firstPos[pin] === undefined) firstPos[pin] = b.pos;
      continue;
    }
    if (level !== 2) continue;
    const meta = (b.node.attrs.meta as Record<string, unknown>) || {};
    const part = meta.modulePart;
    if (part === 1 || part === 2 || part === 3) {
      if (firstPos[part] === undefined) firstPos[part] = b.pos;
      hasArticle[part] = true;
    } else if (firstUnassignedPos === undefined) {
      firstUnassignedPos = b.pos;
    }
  }

  const docEnd = state.doc.content.size;
  const anyFound = MODULE_PARTS.some((p) => firstPos[p] !== undefined);
  const positions: Record<ModulePart, number> = { 1: docEnd, 2: docEnd, 3: docEnd };
  if (anyFound) {
    // Fill the parts nobody has assigned yet with the position of the NEXT
    // part that has one (else the document's end), high to low — so an
    // empty part's header still sits in 1-2-3 order relative to the parts
    // around it instead of floating to the wrong end.
    for (let p = 3; p >= 1; p--) {
      const part = p as ModulePart;
      positions[part] =
        firstPos[part] !== undefined
          ? firstPos[part]!
          : p < 3
            ? positions[(p + 1) as ModulePart]
            : docEnd;
    }
  }
  // Nothing assigned anywhere yet (a brand-new module): the three empty
  // headers stay at `docEnd`, BELOW the unassigned starter article rather
  // than stacked above it. Stacking them on top used to put the one
  // unassigned article under "PART 3", which is exactly the "unassigned
  // looks assigned" confusion the Unassigned header exists to end.

  return {
    positions,
    hasContent: hasArticle,
    unassignedPos: firstUnassignedPos,
  };
}

function buildPartHeaders(state: EditorState): DecorationSet {
  const { positions, hasContent: hasAny, unassignedPos } = modulePartHeaderLayout(state);

  const decorations = MODULE_PARTS.map((part) => {
    const hasContent = hasAny[part];
    // ProseMirror's decoration diffing treats two widgets with the SAME
    // `key` as equal and reuses the old DOM node WITHOUT calling `toDOM`
    // again (`WidgetType.eq` in prosemirror-view) — so the key has to
    // change whenever what the widget RENDERS changes, or a part that goes
    // from empty to having its first article keeps showing "No articles
    // yet" forever (Module Zones Phase 2/3 — "New paragraph"/a drag landing
    // in a previously-empty part must flip this immediately).
    return Decoration.widget(positions[part], () => headerWidget(part, hasContent), {
      side: -1,
      key: `sf-mod-part-hdr-${part}-${hasContent ? 1 : 0}`,
    });
  });
  if (unassignedPos !== undefined) {
    decorations.push(
      Decoration.widget(unassignedPos, () => unassignedHeaderWidget(), {
        side: -1,
        key: 'sf-mod-unassigned-hdr',
      })
    );
  }
  return DecorationSet.create(state.doc, decorations);
}

/** The three PART headers — module mode only. Off entirely on a spec
 *  canvas, the same way ModuleBuildFlags is. */
export const ModulePartHeaders = Extension.create({
  name: 'moduleBuildPartHeaders',
  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: modulePartHeadersKey,
        state: {
          init: (_config, state) => buildPartHeaders(state),
          apply: (tr, value, _old, newState) =>
            tr.docChanged ? buildPartHeaders(newState) : value.map(tr.mapping, tr.doc),
        },
        props: {
          decorations: (state) => modulePartHeadersKey.getState(state) ?? DecorationSet.empty,
        },
      }),
    ];
  },
});

/* ═══════════════════════════════════════════════════════════════
   NEW ARTICLES DEFAULT TO THE PART THEY LAND IN (Task 4).

   A level-2 article with no `modulePart` yet is a `no-part` violation
   (spec-modules.ts, Ruling 6) — right for a genuinely new module (nobody
   has said anything yet), wrong for the ordinary case of typing another
   article under an already-assigned Part 2 run. This fills it in
   automatically the moment such an article appears RIGHT AFTER one that
   already declares a part — Enter-splitting an article, Mod+Enter, or
   promoting a paragraph to level 2 all land here, because all of them are
   just a doc change this plugin sees. The very first article in an
   otherwise-empty module is deliberately left alone: nothing precedes it
   to inherit from, so it still asks a person.
   ═══════════════════════════════════════════════════════════════ */

export const ModuleDefaultPart = Extension.create({
  name: 'moduleDefaultPart',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('moduleDefaultPart'),
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((t) => t.docChanged)) return null;
          let tr: Transaction | null = null;
          let lastPart: ModulePart | undefined;
          for (const b of topBlocks(newState)) {
            const origLevel = b.node.attrs.level as number;
            const meta = (b.node.attrs.meta as Record<string, unknown>) || {};
            if (origLevel > 2) {
              // A PINNED paragraph (TWEAKS item 10) is the running part from
              // here on. A pin that says what the article above already says
              // is noise — an Enter-split copy, or an article that has since
              // arrived above it — so it is dropped in the same transaction.
              const pin = paragraphPin(b.node);
              if (pin === undefined) continue;
              if (pin !== lastPart) {
                lastPart = pin;
                continue;
              }
              const pos = tr ? tr.mapping.map(b.pos) : b.pos;
              tr = (tr || newState.tr).setNodeMarkup(pos, undefined, {
                ...b.node.attrs,
                meta: withoutPin(meta),
              });
              continue;
            }
            if (origLevel !== 1 && origLevel !== 2) continue;
            const clamp = origLevel === 1;
            const raw = meta.modulePart;
            if (!clamp && 'modulePin' in meta) {
              // A pinned paragraph PROMOTED to an article takes its pin as
              // its part — it is the article that part was waiting for.
              const pinned = meta.modulePin;
              const part: ModulePart | undefined =
                raw === 1 || raw === 2 || raw === 3
                  ? raw
                  : pinned === 1 || pinned === 2 || pinned === 3
                    ? pinned
                    : lastPart;
              const pos = tr ? tr.mapping.map(b.pos) : b.pos;
              const next = withoutPin(meta);
              tr = (tr || newState.tr).setNodeMarkup(pos, undefined, {
                ...b.node.attrs,
                meta: part !== undefined ? { ...next, modulePart: part } : next,
              });
              if (part !== undefined) lastPart = part;
              continue;
            }
            if (!clamp && (raw === 1 || raw === 2 || raw === 3)) {
              lastPart = raw;
              continue;
            }
            if (clamp) {
              // Parts are fixed structure — a module canvas has no level-1
              // Part block. Any level-1 content that lands here (paste,
              // import, promote) is clamped down to level 2 in the SAME
              // transaction, defaulting into whatever part precedes it
              // exactly like an ordinary new article would (Job A5).
              const nextMeta = lastPart !== undefined ? { ...meta, modulePart: lastPart } : meta;
              const pos = tr ? tr.mapping.map(b.pos) : b.pos;
              tr = (tr || newState.tr).setNodeMarkup(pos, undefined, {
                ...b.node.attrs,
                level: 2,
                meta: nextMeta,
              });
              continue;
            }
            if (lastPart === undefined) continue;
            const pos = tr ? tr.mapping.map(b.pos) : b.pos;
            tr = (tr || newState.tr).setNodeMarkup(pos, undefined, {
              ...b.node.attrs,
              meta: { ...meta, modulePart: lastPart },
            });
          }
          return tr;
        },
      }),
    ];
  },
});

/* ═══════════════════════════════════════════════════════════════
   DRAG SETS THE PART (Task 3).

   Reordering already lives in `blockSelect.moveBlocksTo` — the gutter's
   own drag and Alt+Shift+Arrow both run it. This is that SAME cut/insert,
   but for a module canvas: when the block being moved is an ARTICLE
   (level 2), the part it lands in is read off whichever already-assigned
   article ends up next to the drop gap, and stamped onto the moved node
   IN THE SAME TRANSACTION — one Ctrl+Z undoes the move and the re-tag
   together, exactly like every other block command here.
   ═══════════════════════════════════════════════════════════════ */

const gidOfNode = (b: TopBlock) => ((b.node.attrs.groupId as string) || '').trim();

export function moveModuleBlocksTo(
  state: EditorState,
  dispatch: Dispatch,
  ids: string[],
  targetIndex: number,
  /** When given, the moved article is stamped with THIS part instead of one
   *  inferred from whatever ends up next to the drop gap — the menu's Part
   *  picker (Cohesion R1 Job A1) knows exactly which part it wants and must
   *  not guess wrong when the target group is empty. */
  forcedPart?: ModulePart,
  /** TWEAKS item 10: a PARAGRAPH run landing under a part that has no
   *  article yet. The run's first block is pinned to this part (`modulePin`)
   *  so the part's header renders above it. Any other paragraph move clears
   *  a pin it carried — wherever it lands, the article above it decides. */
  pinPart?: ModulePart
): boolean {
  const all = topBlocks(state);
  if (!all.length) return false;
  // Callers (gutter drag, Part drop, Alt+Shift+Arrow) hand over whole groups
  // already; an explicit lone-member move here still adopts / leaves a group.
  const want = new Set(ids);
  const moving = all.filter((b) => want.has(b.id));
  if (!moving.length) return false;
  const movingGroup = moving.some((b) => gidOfNode(b));

  const target = Math.max(0, Math.min(targetIndex, all.length));
  const firstIdx = all.findIndex((b) => b.id === moving[0].id);
  const contiguous = moving.every((b, i) => all[firstIdx + i] === b);
  const sameSlot = contiguous && (target === firstIdx || target === firstIdx + moving.length);
  const leadIsParagraph = (moving[0].node.attrs.level as number) > 2;
  const pinFor = (b: TopBlock, i: number): ModulePart | undefined =>
    (b.node.attrs.level as number) > 2 && i === 0 && leadIsParagraph ? pinPart : undefined;
  if (sameSlot) {
    // Same slot: a silent no-op — unless the only change asked for is the
    // pin (a paragraph stepping into / out of an empty part by keyboard).
    let tr: Transaction | null = null;
    moving.forEach((b, i) => {
      if ((b.node.attrs.level as number) <= 2) return;
      const pin = pinFor(b, i);
      const meta = (b.node.attrs.meta as Record<string, unknown>) || {};
      if (paragraphPin(b.node) === pin && (pin !== undefined || !('modulePin' in meta))) return;
      const next = withoutPin(meta);
      tr = (tr || state.tr).setNodeMarkup(b.pos, undefined, {
        ...b.node.attrs,
        meta: pin !== undefined ? { ...next, modulePin: pin } : next,
      });
    });
    if (!tr) return false;
    if (dispatch) dispatch(tr);
    return true;
  }

  // The part signal at the drop gap: the nearest ARTICLE that is not part of
  // the move, preferring the one just before the gap — a run dropped into a
  // part's group usually lands after something already in it — and falling
  // back to the one just after (dropped ahead of everything, or at the top).
  const partAt = (i: number): ModulePart | undefined => {
    const b = all[i];
    if (!b || want.has(b.id)) return undefined;
    // A pinned paragraph speaks for its (article-less) part.
    if ((b.node.attrs.level as number) > 2) return paragraphPin(b.node);
    if ((b.node.attrs.level as number) !== 2) return undefined;
    const meta = (b.node.attrs.meta as Record<string, unknown>) || {};
    return meta.modulePart === 1 || meta.modulePart === 2 || meta.modulePart === 3
      ? (meta.modulePart as ModulePart)
      : undefined;
  };
  let landingPart: ModulePart | undefined = forcedPart;
  if (landingPart === undefined) {
    for (let i = target - 1; i >= 0 && landingPart === undefined; i--) landingPart = partAt(i);
    for (let i = target; i < all.length && landingPart === undefined; i++) landingPart = partAt(i);
  }

  const anchorPos = target < all.length ? all[target].pos : state.doc.content.size;
  const article = (moving[0].node.attrs.level as number) === 2 ? moving[0] : null;
  // A group stamps EVERY article member with the landing part, not just the lead.
  const stampsPart = (b: TopBlock) =>
    b === article || (movingGroup && (b.node.attrs.level as number) === 2);

  // A landing INSIDE somebody else's group is only ever offered where the
  // module rules make it legal (directly under an article heading — the
  // gutter line and the keyboard both snap everywhere else), so the moved
  // run joins that group and the group stays one contiguous run.
  let before: TopBlock | undefined;
  for (let i = target - 1; i >= 0 && !before; i--) if (!want.has(all[i].id)) before = all[i];
  let after: TopBlock | undefined;
  for (let i = target; i < all.length && !after; i++) if (!want.has(all[i].id)) after = all[i];
  const gidOf = (b: TopBlock | undefined) => ((b?.node.attrs.groupId as string) || '').trim();
  const adopt = gidOf(before) && gidOf(before) === gidOf(after) ? gidOf(before) : '';

  const nodes = moving.map((b, i) => {
    let attrs: Record<string, unknown> | null = null;
    const meta = (b.node.attrs.meta as Record<string, unknown>) || {};
    if (stampsPart(b) && landingPart !== undefined) {
      attrs = { ...b.node.attrs, meta: { ...meta, modulePart: landingPart } };
    } else if ((b.node.attrs.level as number) > 2) {
      const pin = pinFor(b, i);
      if (pin !== undefined || 'modulePin' in meta) {
        const next = withoutPin(meta);
        attrs = { ...b.node.attrs, meta: pin !== undefined ? { ...next, modulePin: pin } : next };
      }
    }
    const own = gidOf(b);
    if (adopt && own !== adopt) attrs = { ...(attrs || b.node.attrs), groupId: adopt };
    else if (!adopt && own && gidOf(before) !== own && gidOf(after) !== own) {
      // Leaving a group whose other members stay behind: it lands on its
      // own, never as a stray piece of a group that is now somewhere else.
      const staying = all.some((x) => !want.has(x.id) && gidOf(x) === own);
      if (staying) attrs = { ...(attrs || b.node.attrs), groupId: '' };
    }
    return attrs ? b.node.type.create(attrs, b.node.content, b.node.marks) : b.node;
  });

  const tr = state.tr;
  for (let i = moving.length - 1; i >= 0; i--) {
    tr.delete(moving[i].pos, moving[i].pos + moving[i].node.nodeSize);
  }
  const landing = tr.mapping.map(anchorPos, -1);
  tr.insert(landing, nodes);
  const caret = Math.min(landing + 1, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(caret)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/* ═══════════════════════════════════════════════════════════════
   A PARAGRAPH CROSSES PARTS (TWEAKS item 10).

   A paragraph has no part of its own — it belongs to the article above it.
   So "drop it in Part 2" means "under one of Part 2's articles":

     · onto a part HEADER → the first block under that part's first
       article (an article run keeps the old rule: end of that part);
     · onto a part with NO article yet → directly under its header, pinned
       there (`modulePin`) with the muted "—" number until an article
       arrives — the "No articles yet" hint stays;
     · a precise line directly under an article heading is always a target,
       even inside a group (the run joins that group).

   Alt+Shift+Arrow walks the same slots, one per press.
   ═══════════════════════════════════════════════════════════════ */

/** Is the boundary before row `index` the line directly under an article
 *  heading? Pure over levels so the gutter (DOM rects) and the keyboard
 *  (document) share it. */
export function isUnderArticleLine(levels: number[], index: number): boolean {
  return index > 0 && index < levels.length && levels[index - 1] === 2 && levels[index] > 2;
}

/** Where a drop ON part `part`'s header lands `ids`, and whether the lead
 *  paragraph is pinned there. */
export function partDropTarget(
  state: EditorState,
  ids: string[],
  part: ModulePart
): { index: number; forcedPart?: ModulePart; pinPart?: ModulePart } {
  const all = topBlocks(state);
  const want = expandToGroups(state.doc, ids);
  const lead = all.find((b) => want.has(b.id));
  if (!lead || (lead.node.attrs.level as number) <= 2) {
    return { index: partGroupLandingIndex(all, part, want), forcedPart: part };
  }
  const first = all.findIndex(
    (b) => !want.has(b.id) && (b.node.attrs.level as number) === 2 && articlePart(b.node) === part
  );
  if (first >= 0) return { index: first + 1 };
  const pinned = all.findIndex((b) => !want.has(b.id) && paragraphPin(b.node) === part);
  if (pinned >= 0) return { index: pinned, pinPart: part };
  return { index: partGroupLandingIndex(all, part, want), pinPart: part };
}

/** The header drop itself — one transaction, one undo step. */
export function dropModuleBlocksOnPart(
  state: EditorState,
  dispatch: Dispatch,
  ids: string[],
  part: ModulePart
): boolean {
  const expanded = expandToGroups(state.doc, ids);
  const whole = [...expanded];
  {
    // The (expanded) set dropped on the part it already fills whole stays put,
    // whether or not expansion grew it — compared against the part's contents.
    const all = topBlocks(state);
    const eff = effectiveParts(all);
    const inPart = all.every((b, i) => !expanded.has(b.id) || eff[i] === part);
    const wholePart = all.every((b, i) => expanded.has(b.id) === (eff[i] === part));
    // A lone block already in the part still goes through the landing rule.
    if (inPart && (expanded.size > 1 || wholePart)) return false;
  }
  const t = partDropTarget(state, whole, part);
  return moveModuleBlocksTo(state, dispatch, whole, t.index, t.forcedPart, t.pinPart);
}

/** Alt+Shift+Arrow on a module canvas — `moveSelectedBlocks`' twin. Same
 *  group law as the spec canvas (naming one block of a group moves the
 *  group), same landing rules as the gutter drag. */
export function moveModuleSelectionBy(
  state: EditorState,
  dispatch: Dispatch,
  ids: string[],
  delta: -1 | 1
): boolean {
  const all = topBlocks(state);
  const want = expandToGroups(state.doc, ids);
  const idx = all.map((b, i) => (want.has(b.id) ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return false;
  const runIds = idx.map((i) => all[i].id);
  const first = idx[0];
  const last = idx[idx.length - 1];
  const lead = all[first];
  const leadIsParagraph = (lead.node.attrs.level as number) > 2;

  if (leadIsParagraph) {
    const eff = effectiveParts(all, want);
    const emptyBetween = (lo: number, hi: number) =>
      MODULE_PARTS.filter((q) => q > lo && q < hi && !partOccupied(all, q, want));
    let prevPart = 0;
    for (let i = first - 1; i >= 0; i--) {
      if (want.has(all[i].id)) continue;
      prevPart = eff[i] ?? 0;
      break;
    }
    const pin = paragraphPin(lead.node);
    if (delta > 0) {
      let nextPart = 4;
      for (let i = last + 1; i < all.length; i++) {
        if (want.has(all[i].id)) continue;
        nextPart = eff[i] ?? 0;
        break;
      }
      const empties = emptyBetween(pin ?? prevPart, nextPart);
      if (empties.length) {
        return moveModuleBlocksTo(state, dispatch, runIds, first, undefined, empties[0]);
      }
    } else if (pin !== undefined) {
      const empties = emptyBetween(prevPart, pin);
      // Up out of a pinned slot: into the empty part above, else back under
      // the article above (same place, pin dropped).
      return moveModuleBlocksTo(
        state,
        dispatch,
        runIds,
        first,
        undefined,
        empties.length ? empties[empties.length - 1] : undefined
      );
    }
  }

  let target = delta < 0 ? first - 1 : last + 2;
  if (target < 0 || target > all.length) return false;
  const levels = all.map((b) => b.node.attrs.level as number);
  target = snapBoundaryOutOfGroup(
    all.map((b) => ({ id: b.id, group: ((b.node.attrs.groupId as string) || '').trim() })),
    target,
    {
      moving: want,
      prefer: delta < 0 ? 'before' : 'after',
      legal: leadIsParagraph ? (i) => isUnderArticleLine(levels, i) : undefined,
    }
  );
  return moveModuleBlocksTo(state, dispatch, runIds, target);
}

/* ═══════════════════════════════════════════════════════════════
   THE PART PICKER RELOCATES (Cohesion R1 Job A1).

   Setting `modulePart` from the block context menu used to stamp the
   metadata in place, leaving the article wherever it already sat in
   document order. A module saved as `[p1, p2, p3, p1]` then rendered TWO
   `1.01`s (`computeModuleNumbering` reseeds `counters[0]` every time the
   part changes) and broke the grouped display the Part headers promise.
   This is the menu's one-transaction, undoable equivalent of a drag: it
   finds the END of the target part's existing group — physically after
   its last article, or right before the next part's group if this one is
   still empty — and moves the article there in the SAME transaction that
   sets `modulePart`, via `moveModuleBlocksTo`'s `forcedPart`.

   A single article is just the one-id case of `assignModulePartMany`
   below (Module Zones Phase 1) — this function delegates there so the
   Blocks toolbox's Part buttons and every earlier single-select caller
   share one rule.
   ═══════════════════════════════════════════════════════════════ */
export function assignModulePart(
  state: EditorState,
  dispatch: Dispatch,
  blockId: string,
  part: ModulePart
): boolean {
  return assignModulePartMany(state, dispatch, [blockId], part);
}

/** The part a node declares, if it is one of the three (a plain read of
 *  `meta.modulePart` — the single source every part-aware operation in this
 *  file shares, so a stray typo never drifts one of them out of sync). */
function articlePart(node: PMNode): ModulePart | undefined {
  const meta = (node.attrs.meta as Record<string, unknown>) || {};
  return meta.modulePart === 1 || meta.modulePart === 2 || meta.modulePart === 3
    ? (meta.modulePart as ModulePart)
    : undefined;
}

/** The part a PARAGRAPH is pinned to (TWEAKS item 10) — set only when a
 *  paragraph is dropped into a part that has no article yet, so the part
 *  header can sit above it. Builder state only: never saved (the save path
 *  strips it, and `validateModuleStructure` flags the paragraph as having
 *  no article above it until one arrives). */
export function paragraphPin(node: PMNode): ModulePart | undefined {
  if ((node.attrs.level as number) <= 2) return undefined;
  const meta = (node.attrs.meta as Record<string, unknown>) || {};
  return meta.modulePin === 1 || meta.modulePin === 2 || meta.modulePin === 3
    ? (meta.modulePin as ModulePart)
    : undefined;
}

function withoutPin(meta: Record<string, unknown>): Record<string, unknown> {
  if (!('modulePin' in meta)) return meta;
  const next = { ...meta };
  delete next.modulePin;
  return next;
}

/** The part every top-level block READS as being in — its own part for an
 *  article, its pin for a pinned paragraph, otherwise whatever runs above
 *  it. `undefined` in the unassigned area (and for `excludeIds`). */
function effectiveParts(
  all: TopBlock[],
  excludeIds: Set<string> = new Set()
): Array<ModulePart | undefined> {
  const out: Array<ModulePart | undefined> = [];
  let cur: ModulePart | undefined;
  for (const b of all) {
    if (excludeIds.has(b.id)) {
      out.push(undefined);
      continue;
    }
    const level = b.node.attrs.level as number;
    if (level === 2) cur = articlePart(b.node);
    else if (level > 2) cur = paragraphPin(b.node) ?? cur;
    out.push(cur);
  }
  return out;
}

/** Whether part `part` holds anything at all — an article or a pinned
 *  paragraph — ignoring `excludeIds`. */
function partOccupied(all: TopBlock[], part: ModulePart, excludeIds: Set<string>): boolean {
  return all.some(
    (b) =>
      !excludeIds.has(b.id) &&
      (((b.node.attrs.level as number) === 2 && articlePart(b.node) === part) ||
        paragraphPin(b.node) === part)
  );
}

/** Where "the end of PART `part`'s group" is, as an index into `all`
 *  (`topBlocks` order) — skipping every block in `excludeIds` as though it
 *  were not there (a multi-drag or a paste's own moving/inserted set).
 *  Shared by the toolbox's Part buttons, the "New paragraph" insert, and
 *  the drag machinery's header/empty-hint drop targets. */
function partGroupLandingIndex(all: TopBlock[], part: ModulePart, excludeIds: Set<string>): number {
  let activePart: ModulePart | undefined;
  let landing = all.length;
  let inGroup = false;
  let hasArticle = false;
  let firstPin = -1;
  for (let i = 0; i < all.length; i++) {
    if (excludeIds.has(all[i].id)) continue;
    const b = all[i];
    if ((b.node.attrs.level as number) === 2) {
      const p = articlePart(b.node);
      if (p !== undefined) activePart = p;
      if (p === part) hasArticle = true;
    } else {
      // A pinned paragraph opens its part's group (TWEAKS item 10).
      const pin = paragraphPin(b.node);
      if (pin !== undefined) activePart = pin;
      if (pin === part && firstPin < 0) firstPin = i;
    }
    if (activePart === part) {
      landing = i + 1;
      inGroup = true;
    } else if (inGroup) {
      break;
    } else if (activePart !== undefined && activePart > part) {
      landing = i;
      break;
    }
  }
  // Only pinned paragraphs so far: the first article lands ABOVE them, so
  // they become its children instead of staying article-less.
  if (!hasArticle && firstPin >= 0) return firstPin;
  return landing;
}

/** Public form of the above — the drag/insert callers hold an `EditorState`,
 *  not a pre-fetched `topBlocks()` list. */
export function partLandingIndex(
  state: EditorState,
  part: ModulePart,
  excludeIds: Set<string> = new Set()
): number {
  return partGroupLandingIndex(topBlocks(state), part, excludeIds);
}

/** The part whose group CONTAINS document position `pos` — the nearest
 *  article at or before it, falling back to the nearest one AFTER when
 *  nothing precedes (pasting/inserting ahead of the first assigned
 *  article). `undefined` only when nothing in the document is assigned to
 *  any part yet (a brand-new, still-unset module). */
function articlePartAt(all: TopBlock[], pos: number): ModulePart | undefined {
  let idx = all.findIndex((b) => pos < b.pos + b.node.nodeSize);
  if (idx < 0) idx = all.length - 1;
  for (let i = idx; i >= 0; i--) {
    const pin = paragraphPin(all[i].node);
    if (pin !== undefined) return pin;
    if ((all[i].node.attrs.level as number) !== 2) continue;
    const p = articlePart(all[i].node);
    if (p !== undefined) return p;
  }
  for (let i = Math.max(idx, 0); i < all.length; i++) {
    if ((all[i].node.attrs.level as number) !== 2) continue;
    const p = articlePart(all[i].node);
    if (p !== undefined) return p;
  }
  return undefined;
}

/** The part the CARET currently sits in — the "New paragraph" popover's
 *  default highlight, and paste's destination for `ModulePasteStamp`
 *  below. */
export function partAtCaret(state: EditorState): ModulePart | undefined {
  return articlePartAt(topBlocks(state), state.selection.from);
}

/** The toolbox drag's own MIME marker (Module Zones Phase 2) — how the
 *  canvas (`BlockGutter`) recognizes a "New paragraph" drag versus a native
 *  OS file/text drop, or a gutter grip's own mouse-only reordering. Both the
 *  toolbox button and the canvas read this SAME string, so the two never
 *  drift out of step. */
export const NEW_PARAGRAPH_DRAG_MIME = 'application/x-sf-new-paragraph';

/* ═══════════════════════════════════════════════════════════════
   "NEW PARAGRAPH" — an empty article, placed by PART rather than by
   caret position (Module Zones Phase 2).

   Both the toolbox's click-popover and its drag-onto-a-zone path land
   here: `insertModuleArticle` takes an explicit index (from a drop
   position or a part's landing index) and, when no part is forced,
   infers one from whichever already-assigned article ends up next to the
   gap — the exact same neighbor rule `moveModuleBlocksTo` uses for a
   drag, so an insert and a move never disagree about what an empty
   position "belongs to".
   ═══════════════════════════════════════════════════════════════ */
export function insertModuleArticle(
  state: EditorState,
  dispatch: Dispatch,
  targetIndex: number,
  /** Set by a header/empty-hint drop or the popover's explicit choice.
   *  Left undefined for an ordinary body drop, which infers from the
   *  blocks the new article is landing between. */
  forcedPart?: ModulePart
): boolean {
  const all = topBlocks(state);
  const target = Math.max(0, Math.min(targetIndex, all.length));
  let landingPart = forcedPart;
  if (landingPart === undefined) {
    for (let i = target - 1; i >= 0 && landingPart === undefined; i--) {
      if ((all[i].node.attrs.level as number) !== 2) continue;
      landingPart = articlePart(all[i].node);
    }
    for (let i = target; i < all.length && landingPart === undefined; i++) {
      if ((all[i].node.attrs.level as number) !== 2) continue;
      landingPart = articlePart(all[i].node);
    }
  }
  // A brand-new, entirely-unassigned module (the starter article included)
  // has nothing to infer from at all — Part 1 is the only sane default for
  // "the first thing landing in it".
  if (landingPart === undefined) landingPart = 1;

  const anchorPos = target < all.length ? all[target].pos : state.doc.content.size;
  const node = state.schema.nodes.specParagraph.create({
    blockId: newBlockId(),
    level: 2,
    continuation: false,
    excluded: false,
    meta: { modulePart: landingPart },
  });
  const tr = state.tr.insert(anchorPos, node);
  const caret = Math.min(anchorPos + 1, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(caret)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/** The popover/drag-onto-header case: always the END of that part's
 *  existing group, empty or not. */
export function insertModuleArticleAtPartEnd(
  state: EditorState,
  dispatch: Dispatch,
  part: ModulePart
): boolean {
  return insertModuleArticle(state, dispatch, partLandingIndex(state, part), part);
}

/* ═══════════════════════════════════════════════════════════════
   PASTE STAMPS THE DESTINATION'S PART (Module Zones Phase 3).

   Cut/copy carries a block's OWN `modulePart` in its native clipboard
   round-trip (ProseMirror serializes every node attribute to the DOM and
   reads it straight back — nothing hand-rolled). Landing it unstamped
   would leave it lying about which part it is actually in, the same way
   `computeModuleNumbering` breaks on a part that lands out of the group
   it claims (`moveModuleBlocksTo`'s own header comment). This rewrites
   every top-level ARTICLE in the pasted slice to the part the caret is
   IN at paste time — one part, because a paste always lands in exactly
   one place — overriding whatever the source carried. `ModuleDefaultPart`
   only ever fills a BLANK part, so it never fights this.

   The same pass also strips `excluded` (Skip at export): a module has no
   export of its own and the toggle is not even offered here, so a paste
   that drags one in from elsewhere (an old insertion, a spec block copied
   across windows) can never resurrect it — the clipboard-path twin of the
   `RUNTIME_KEYS` strip `spec-modules.ts` runs when a spec block is FIRST
   captured into a module.
   ═══════════════════════════════════════════════════════════════ */
export function stampModulePaste(fragment: Fragment, part: ModulePart): Fragment {
  const children: PMNode[] = [];
  let touched = false;
  fragment.forEach((node) => {
    let next = node;
    const isArticle = (next.attrs.level as number) === 2 && !!next.attrs.blockId;
    const needsPart = isArticle && articlePart(next) !== part;
    const hasExcluded = 'excluded' in next.attrs && !!next.attrs.excluded;
    const rawMeta = (next.attrs.meta as Record<string, unknown>) || {};
    // A pin belongs to where the paragraph WAS (TWEAKS item 10) — a pasted
    // copy lands under whatever article the caret is in.
    const hasPin = !!next.attrs.blockId && 'modulePin' in rawMeta;
    if (needsPart || hasExcluded || hasPin) {
      const meta = needsPart
        ? { ...withoutPin(rawMeta), modulePart: part }
        : hasPin
          ? withoutPin(rawMeta)
          : next.attrs.meta;
      next = next.type.create(
        { ...next.attrs, ...(hasExcluded ? { excluded: false } : {}), meta },
        next.content,
        next.marks
      );
      touched = true;
    }
    if (next.content && next.content.childCount) {
      const mapped = stampModulePaste(next.content, part);
      if (mapped !== next.content) {
        next = next.copy(mapped);
        touched = true;
      }
    }
    children.push(next);
  });
  return touched ? Fragment.fromArray(children) : fragment;
}

/** Wired into the module canvas only (`SpecStudioCanvas`, alongside
 *  `ModuleBuildFlags`/`ModulePartHeaders`/`ModuleDefaultPart`) — off
 *  entirely on a spec canvas, where parts mean nothing. Reads the
 *  DESTINATION off `view.state.selection` (the caret BEFORE the paste
 *  replaces it), same rule the "New paragraph" popover's default uses. */
export const ModulePasteStamp = Extension.create({
  name: 'modulePasteStamp',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('modulePasteStamp'),
        props: {
          transformPasted(slice, view) {
            const part = partAtCaret(view.state);
            if (part === undefined) return slice;
            const content = stampModulePaste(slice.content, part);
            return content === slice.content
              ? slice
              : new Slice(content, slice.openStart, slice.openEnd);
          },
        },
      }),
    ];
  },
});

/* ═══════════════════════════════════════════════════════════════
   THE PART PICKER, MULTI-SELECT (Module Zones Phase 1).

   The Blocks toolbox Part group works on however many level-2 articles the
   gutter has selected, not just one. Same law as `assignModulePart`: set
   `modulePart` and relocate to the end of the target part's group, all of
   it in ONE transaction — but now for a whole (possibly non-contiguous) set
   of articles, so ONE Ctrl+Z restores every article's part AND position.

   An article's DEEPER CHILDREN — every block after it whose level is
   greater than 2, up to the next level ≤ 2 block — ride along. They carry
   no `modulePart` of their own (only articles do; `computeModuleNumbering`
   and this file's own `ModuleDefaultPart` both read a child's part off
   whichever article precedes it), so leaving one behind while its article
   moves would silently re-home it under whatever now sits in the gap.
   ═══════════════════════════════════════════════════════════════ */
export function assignModulePartMany(
  state: EditorState,
  dispatch: Dispatch,
  blockIds: string[],
  part: ModulePart
): boolean {
  const all = topBlocks(state);
  const idSet = new Set(blockIds);
  // Only level-2 articles among the given ids are targets — the toolbox
  // only ever offers this on a uniform-article selection, but a stray id
  // (e.g. a block that left the document) is ignored rather than corrupting
  // the group.
  const targets = all.filter((b) => idSet.has(b.id) && (b.node.attrs.level as number) === 2);
  if (!targets.length) return false;

  if (targets.every((t) => articlePart(t.node) === part)) return false; // already uniform

  // Containment: gather each target article plus every block right after it
  // whose level is deeper (> 2), stopping at the next level ≤ 2 block.
  // Collected in ONE pass over `all` so a scattered multi-select still
  // yields one document-ordered run.
  const movingSet = new Set<string>();
  for (let i = 0; i < all.length; i++) {
    if (!idSet.has(all[i].id) || (all[i].node.attrs.level as number) !== 2) continue;
    movingSet.add(all[i].id);
    for (let j = i + 1; j < all.length && (all[j].node.attrs.level as number) > 2; j++) {
      movingSet.add(all[j].id);
    }
  }
  const moving = all.filter((b) => movingSet.has(b.id));
  if (!moving.length) return false;

  // Where "the end of the target part's group" is — `assignModulePart`'s
  // single-article scan, generalized to skip every moving block instead of
  // just one. Shared with the insert/drag paths (Module Zones Phase 2/3) as
  // `partLandingIndex`.
  const landing = partGroupLandingIndex(all, part, movingSet);

  const target = Math.max(0, Math.min(landing, all.length));
  const firstIdx = all.findIndex((b) => movingSet.has(b.id));
  const contiguous = moving.every((b, i) => all[firstIdx + i] === b);
  const needsMove = !(contiguous && (target === firstIdx || target === firstIdx + moving.length));

  const stampedNode = (b: (typeof all)[number]) => {
    if ((b.node.attrs.level as number) !== 2) return b.node;
    const meta = { ...((b.node.attrs.meta as Record<string, unknown>) || {}), modulePart: part };
    return b.node.type.create({ ...b.node.attrs, meta }, b.node.content, b.node.marks);
  };

  if (!needsMove) {
    // Already sitting exactly where the group ends — only the part
    // attribute (on whichever targets don't already carry it) needs a set.
    let tr: Transaction | null = null;
    for (const b of moving) {
      if ((b.node.attrs.level as number) !== 2 || articlePart(b.node) === part) continue;
      const meta = { ...((b.node.attrs.meta as Record<string, unknown>) || {}), modulePart: part };
      const pos = tr ? tr.mapping.map(b.pos) : b.pos;
      tr = (tr || state.tr).setNodeMarkup(pos, undefined, { ...b.node.attrs, meta });
    }
    if (!tr) return false;
    if (dispatch) dispatch(tr);
    return true;
  }

  const anchorPos = target < all.length ? all[target].pos : state.doc.content.size;
  const nodes = moving.map(stampedNode);

  const tr = state.tr;
  for (let i = moving.length - 1; i >= 0; i--) {
    tr.delete(moving[i].pos, moving[i].pos + moving[i].node.nodeSize);
  }
  const landingPos = tr.mapping.map(anchorPos, -1);
  tr.insert(landingPos, nodes);
  const caret = Math.min(landingPos + 1, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(caret)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}
